import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Portal from "./Portal";
import { MoreVertical } from "@/components/icons";

/**
 * Three-dot action menu anchored to a button. The popover is rendered through
 * a Portal so it never gets clipped by table/card overflow.
 *
 * Two-phase render to avoid the "first click animates from the wrong origin"
 * glitch:
 *   1. Mount the menu invisibly to obtain its real height via refs.
 *   2. Compute the trigger-relative position + transform-origin, then mark
 *      it `ready` — only then the animate-in CSS class is applied, so the
 *      zoom-in animation always emanates from the dot the user clicked.
 *
 * Each item: { icon, label, onClick, testId, color?: "default"|"red"|"emerald", divider? }
 */
export default function RowActionsMenu({
  items = [],
  triggerTestId,
  title = "Más acciones",
  buttonClassName = "w-8 h-8 rounded-full flex items-center justify-center text-black/55 hover:text-black hover:bg-black/[0.06] transition active:scale-90",
}) {
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, origin: "50% 0%" });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const compute = () => {
    if (!btnRef.current || !menuRef.current) return null;
    const r = btnRef.current.getBoundingClientRect();
    const menuW = menuRef.current.offsetWidth || 232;
    const menuH = menuRef.current.offsetHeight || 230;
    const margin = 10;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < menuH + margin;

    const top = openUp ? r.top - menuH - 8 : r.bottom + 8;
    let left = r.right - menuW;
    if (left < margin) left = margin;

    const originX = r.left + r.width / 2 - left;
    const originY = r.top + r.height / 2 - top;
    return { top, left, origin: `${originX}px ${originY}px` };
  };

  // First paint after open: menu mounts invisibly, compute exact pos, then
  // flip ready=true so the animation runs from the right origin.
  useLayoutEffect(() => {
    if (!open) {
      setReady(false);
      return;
    }
    const p = compute();
    if (p) setPos(p);
    // Defer the visibility/animation flip so the position is committed first.
    const raf = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Keep menu glued to the trigger while open: refresh on scroll/resize.
  useEffect(() => {
    if (!open) return;
    const handler = () => {
      const p = compute();
      if (p) setPos(p);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const close = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const colorClasses = {
    default: "text-black hover:bg-white",
    red: "text-red-600 hover:bg-white",
    emerald: "text-[#019A75] hover:bg-white",
    sky: "text-sky-700 hover:bg-white",
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={triggerTestId}
        title={title}
        className={buttonClassName}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <Portal>
          <div
            ref={menuRef}
            data-state="open"
            role="menu"
            style={{
              top: pos.top,
              left: pos.left,
              width: 232,
              transformOrigin: pos.origin,
              visibility: ready ? "visible" : "hidden",
              pointerEvents: ready ? "auto" : "none",
            }}
            className={`fixed z-[60] bg-white rounded-[28px] border border-black/[0.06] shadow-[0_22px_60px_-14px_rgba(0,0,0,0.18),0_4px_18px_-2px_rgba(0,0,0,0.06)] p-2 ${
              ready ? "animate-in fade-in zoom-in-50 duration-200 ease-out" : ""
            }`}
          >
            {items.map((it, idx) => {
              if (it.divider) {
                return (
                  <div
                    key={`d-${idx}`}
                    className="my-2 mx-3 border-t border-black/[0.08]"
                  />
                );
              }
              const Icon = it.icon;
              const color = colorClasses[it.color] || colorClasses.default;
              return (
                <button
                  key={it.testId || idx}
                  type="button"
                  data-testid={it.testId}
                  onClick={() => {
                    setOpen(false);
                    it.onClick?.();
                  }}
                  role="menuitem"
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-[13px] font-bold rounded-2xl transition ${color}`}
                >
                  {Icon ? <Icon className="w-4 h-4 flex-shrink-0" /> : null}
                  <span className="truncate text-left flex-1">{it.label}</span>
                </button>
              );
            })}
          </div>
        </Portal>
      )}
    </>
  );
}
